import { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../services/api";
import { AuthContext } from "../services/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import { useBranding } from "../theme/BrandingContext";
import {
  Sun,
  Moon,
  Monitor as MonitorIcon,
  Eye,
  EyeOff,
  LogIn,
  Activity,
  Camera,
  BarChart3,
  ShieldCheck,
  KeyRound,
} from "lucide-react";
import { COPYRIGHT_TEXT } from "../constants/legal";
import { baseUrl } from "../services/BaseURL";

/* Highlights shown on the branded hero panel. LiMo = Live Monitoring. */
const FEATURES = [
  { icon: Activity, title: "Live activity tracking", desc: "See active apps, windows and idle time in real time." },
  { icon: Camera, title: "Screenshot monitoring", desc: "Automatic, privacy-aware screen captures on a schedule." },
  { icon: BarChart3, title: "Productivity analytics", desc: "Dashboards, leaderboards and exportable reports." },
  { icon: ShieldCheck, title: "Secure attendance", desc: "Face-verified check-ins and tamper-resistant logs." },
];

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  // Which federated providers the admin has switched on, if any.
  const [sso, setSso] = useState({
    enabled: false, oidc: false, saml: false, kerberos: false, label: "",
  });

  const { login } = useContext(AuthContext);
  const { mode, setMode, resolved } = useTheme();
  const branding = useBranding();
  const navigate = useNavigate();

  // Windows Integrated Login (Kerberos/SPNEGO). On a domain-joined PC the
  // browser answers the 401 Negotiate challenge with a ticket automatically
  // and the user is in without typing anything.
  //
  // Gated on sso.kerberos so we don't fire a guaranteed-to-fail request on
  // every login-page load when the feature is switched off. `credentials:
  // "include"` is required — without it the browser withholds the ticket.
  useEffect(() => {
    if (!sso.kerberos) return;
    let cancelled = false;

    const probeKerberos = async () => {
      try {
        const res = await fetch(`${baseUrl}/auth/kerberos`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (cancelled || !res.ok) return;
        const data = await res.json().catch(() => null);
        if (cancelled || !data?.token) return;
        login(data.token, { permissions: data.permissions || [] });
        navigate("/admin", { replace: true });
      } catch {
        // Silent fallback to the normal username/password form.
      }
    };

    probeKerberos();
    return () => {
      cancelled = true;
    };
  }, [login, navigate, sso.kerberos]);

  // Ask the backend which SSO providers are configured. Failure is silent and
  // non-blocking: if this call errors the page is simply the password form,
  // which is exactly the fallback we want when the IdP config is broken.
  useEffect(() => {
    let cancelled = false;
    API.get("/auth/sso/providers")
      .then((res) => {
        if (!cancelled && res.data) setSso(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // A full-page navigation, not an XHR — the IdP has to render its own login
  // and MFA screens, and it will set cookies we must not proxy.
  const startSso = (protocol) => {
    const base = baseUrl.replace(/\/+$/, "");
    window.location.href = `${base}/auth/sso/start?protocol=${protocol}&client=web`;
  };

  // Surface the reason when /sso/callback bounced the user back here.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get("ssoError");
    if (ssoError) {
      setError(ssoError);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await API.post("/auth/login", { username, password });
      // Pass permissions from the login response so AuthContext caches them
      // and downstream RequirePermission gates work on the very next render.
      login(res.data.token, { permissions: res.data.permissions });
      navigate("/admin", { replace: true });
    } catch (err) {
      /* Distinguish "wrong password" from "server not reachable".
       *
       * Every failure used to read "Invalid username/email or password",
       * including a 502 from the proxy while the backend was still starting.
       * That is actively misleading: it sends people to reset a password that
       * was never wrong, and it hides a real outage behind a user error. The
       * all-in-one container makes this routine — nginx serves the login page
       * within 5 seconds while the JVM needs ~30, so there is a half-minute
       * window after every deploy where sign-in legitimately cannot work yet.
       */
      const status = err?.response?.status;

      if (status === 401 || status === 403) {
        setError("Invalid username/email or password");
      } else if (status === 502 || status === 503 || status === 504) {
        setError(
          "The server is still starting up. Wait about 30 seconds and try again."
        );
      } else if (status === 429) {
        setError("Too many attempts. Please wait a minute and try again.");
      } else if (!err?.response) {
        setError(
          "Could not reach the server. Check your connection and try again."
        );
      } else {
        setError(
          err?.response?.data?.message ||
            "Sign-in failed. Please try again, or contact your administrator."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const cycleMode = () => {
    const next = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
    setMode(next);
  };

  const themeIcon = mode === "system"
    ? <MonitorIcon size={18} />
    : resolved === "dark" ? <Moon size={18} /> : <Sun size={18} />;

  const primary = branding.primaryColor;
  const accent = branding.accentColor;
  const brandGradient = `linear-gradient(135deg, ${primary}, ${accent})`;

  const Logo = ({ size = 14 }) =>
    branding.logoUrl ? (
      <img
        src={branding.logoUrl}
        alt={branding.companyName}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        className="object-contain rounded-2xl bg-white shadow-lg"
        style={{ width: size * 4, height: size * 4 }}
      />
    ) : (
      <div
        className="rounded-2xl text-white flex items-center justify-center font-bold shadow-lg"
        style={{
          width: size * 4,
          height: size * 4,
          fontSize: size * 1.4,
          background: brandGradient,
        }}
      >
        {(branding.companyName || "P").charAt(0).toUpperCase()}
      </div>
    );

  return (
    <div className="min-h-screen flex bg-white dark:bg-slate-950 transition-colors">

      {/* ─── Left: branded hero panel (hidden on small screens) ─── */}
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between p-12 overflow-hidden text-white"
        style={{ background: brandGradient }}
      >
        {/* decorative blurred blobs */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute top-1/3 -right-20 w-80 h-80 rounded-full bg-black/10 blur-3xl" />
          <div className="absolute -bottom-28 left-1/4 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>

        {/* top: logo + name */}
        <div className="relative flex items-center gap-3">
          <Logo size={12} />
          <span className="text-xl font-bold tracking-tight drop-shadow-sm">
            {branding.companyName || "LiMo"}
          </span>
        </div>

        {/* middle: headline + features */}
        <div className="relative max-w-md">
          <h1 className="text-4xl font-bold leading-tight drop-shadow-sm">
            Live Monitoring,
            <br />
            made effortless.
          </h1>
          <p className="mt-4 text-white/80 text-base leading-relaxed">
            Track productivity, attendance and activity across your whole team —
            all from one secure dashboard.
          </p>

          <ul className="mt-10 space-y-5">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex items-start gap-4">
                <span className="flex-shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-white/15 backdrop-blur ring-1 ring-white/20">
                  <Icon size={20} />
                </span>
                <div>
                  <p className="font-semibold leading-tight">{title}</p>
                  <p className="text-sm text-white/75 leading-snug">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* bottom: copyright */}
        <p className="relative text-xs text-white/70">{COPYRIGHT_TEXT}</p>
      </div>

      {/* ─── Right: login form ─── */}
      <div className="relative flex w-full lg:w-1/2 items-center justify-center px-4 py-10 bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

        {/* Floating theme switcher */}
        <button
          type="button"
          onClick={cycleMode}
          aria-label={`Theme: ${mode}`}
          className="absolute top-4 right-4 p-2 rounded-xl bg-white/70 dark:bg-slate-800/70 backdrop-blur text-gray-700 dark:text-gray-200 shadow hover:shadow-md active:scale-95 transition"
        >
          {themeIcon}
        </button>

        <form onSubmit={handleSubmit} className="w-full max-w-sm">
          {/* Header — logo shows on mobile where the hero panel is hidden */}
          <div className="flex flex-col items-center mb-8">
            <div className="lg:hidden mb-3">
              <Logo size={14} />
            </div>
            <h2
              className="text-2xl font-bold bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(90deg, ${primary}, ${accent})` }}
            >
              {branding.companyName || "Admin"} Admin
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Welcome back — sign in to continue
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-sm"
            >
              {error}
            </div>
          )}

          {/* Single sign-on. Rendered above the form because it's the
              preferred path when configured — but the password form stays,
              so a broken IdP can never lock every administrator out. */}
          {sso.enabled && (
            <>
              <div className="space-y-2 mb-5">
                {sso.oidc && (
                  <SsoButton
                    label={sso.label || "Sign in with SSO"}
                    onClick={() => startSso("oidc")}
                  />
                )}
                {sso.saml && (
                  <SsoButton
                    label={sso.oidc ? "Sign in with SAML" : sso.label || "Sign in with SSO"}
                    onClick={() => startSso("saml")}
                  />
                )}
              </div>

              <div className="flex items-center gap-3 mb-5">
                <span className="h-px flex-1 bg-gray-200 dark:bg-slate-700" />
                <span className="text-[11px] uppercase tracking-wide text-gray-400">
                  or sign in with a password
                </span>
                <span className="h-px flex-1 bg-gray-200 dark:bg-slate-700" />
              </div>
            </>
          )}

          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">
            Username or Email
          </label>
          <input
            className="w-full mb-4 p-3 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
            placeholder="your.username or email@example.com"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
          />

          <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">
            Password
          </label>
          <div className="relative mb-6">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full p-3 pr-11 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* Forgot password — opens an inline modal so admins don't lose
              the half-typed username field by navigating away. */}
          <div className="-mt-4 mb-4 text-right">
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ backgroundImage: `linear-gradient(90deg, ${primary}, ${accent})` }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium text-white hover:shadow-lg hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="inline-block w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
            ) : (
              <LogIn size={18} />
            )}
            {loading ? "Signing in..." : "Login"}
          </button>

          {/* Privacy policy.
              Reachable from the sign-in page on purpose: it is the URL given to
              Google Play, and both a reviewer and an employee must be able to
              find it WITHOUT an account. A policy that can only be reached from
              inside the app is not a published policy. */}
          <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
            <Link to="/privacy" className="hover:underline">
              Privacy Policy
            </Link>
          </p>

          {/* Copyright for mobile, where the hero panel (and its copyright) is hidden */}
          <p className="lg:hidden mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            {COPYRIGHT_TEXT}
          </p>
        </form>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}

/**
 * Neutral, outlined SSO button. Deliberately unbranded — the same control
 * serves Entra ID, Google Workspace, Okta or Keycloak, and the admin supplies
 * the wording via Settings → Security.
 */
function SsoButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 active:scale-[0.98] transition"
    >
      <KeyRound size={18} />
      {label}
    </button>
  );
}

/**
 * Inline modal that POSTs /auth/forgot-password.  Pulled out as a sibling
 * component (not its own file) because it's tiny and only ever used here.
 * The backend always returns 200, so we always show the same success state
 * — that's deliberate, to avoid leaking which addresses are registered.
 */
function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      setLoading(true);
      await API.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't reach the server. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Check your inbox
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              If an account exists for <strong>{email}</strong>, a reset link
              is on its way. The link expires in 30 minutes.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Forgot password?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Enter your account email and we'll send you a reset link.
            </p>
            <input
              type="email"
              autoFocus
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-gray-100 mb-3"
            />
            {error && (
              <p className="text-sm text-red-600 mb-3">{error}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
